import React from 'react';
import { useView } from '../../contexts/ViewContext';
import DashboardGreeting from './DashboardGreeting';
import UpsellTile from './UpsellTile';
import { FEATURE_CATALOG } from './featureCatalog';
import RevenueHighlight from './highlights/RevenueHighlight';
import RoasHighlight from './highlights/RoasHighlight';
import VisitorsHighlight from './highlights/VisitorsHighlight';
import AiVisibilityHighlight from './highlights/AiVisibilityHighlight';

// Chrome-less body of the new dashboard: greeting + highlights strip + tile
// grid + upsell. Shared between admin `/dashboard` and client `/client-dashboard`
// so both surfaces stay in lockstep — each caller wraps this in its own
// AdminHeader/ClientHeader + NavSidebar.
export default function DashboardTileGrid() {
  const view = useView();
  const clientId = view.supabaseClientId;
  const clientName = view.clientDisplayName || view.selectedProject?.name || null;

  const entitled = FEATURE_CATALOG.filter((f) => view[f.key] && f.LiveTile);
  const upsell = FEATURE_CATALOG.filter((f) => !view[f.key] || !f.LiveTile);

  const highlightsCount =
    (view.showBookings ? 1 : 0)
    + (view.showGoogleAds ? 1 : 0)
    + (view.showCdp ? 1 : 0)
    + (view.showAim ? 1 : 0);

  const tileProps = { clientId, rangeStart: view.rangeStart, rangeEnd: view.rangeEnd };

  return (
    <>
      <DashboardGreeting clientName={clientName} />

      {highlightsCount > 0 && (
        <section aria-labelledby="highlights-heading" className="mb-10">
          <h2 id="highlights-heading" className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4">
            Today's Highlights
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {view.showBookings && <RevenueHighlight {...tileProps} />}
            {view.showGoogleAds && <RoasHighlight {...tileProps} />}
            {view.showCdp && <VisitorsHighlight {...tileProps} />}
            {view.showAim && <AiVisibilityHighlight clientId={clientId} />}
          </div>
        </section>
      )}

      {entitled.length > 0 && (
        <section aria-labelledby="dashboards-heading" className="mb-10">
          <h2 id="dashboards-heading" className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4">
            Your Dashboards
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {entitled.map((f) => {
              const T = f.LiveTile;
              return <T key={f.key} {...tileProps} />;
            })}
          </div>
        </section>
      )}

      {upsell.length > 0 && (
        <section aria-labelledby="explore-heading">
          <div className="flex items-baseline justify-between mb-4">
            <h2 id="explore-heading" className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {entitled.length === 0 ? 'Available for this property' : 'Explore more'}
            </h2>
            {entitled.length === 0 && clientId && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No dashboards are enabled here yet. Ask your account manager to turn any of these on.
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upsell.map((f) => (
              <UpsellTile key={f.key} title={f.title} icon={f.icon} pitch={f.pitch} />
            ))}
          </div>
        </section>
      )}

      {!clientId && (
        <div className="mt-12 text-center text-sm text-gray-500 dark:text-gray-400">
          Pick a property from the header above to see its dashboards.
        </div>
      )}
    </>
  );
}
